/** BroadcastChannel name for Google Sheets OAuth popup ↔ opener communication */
export const GOOGLE_SHEETS_OAUTH_CHANNEL = 'google_sheets_oauth';

export interface GoogleSheetsOAuthTokenPayload {
  access_token: string;
  refresh_token: string;
  expiry_date: string;
  email: string;
}

export type GoogleSheetsOAuthMessage =
  { type: 'tokens'; payload: GoogleSheetsOAuthTokenPayload } | { type: 'cancelled' };

const isBroadcastChannelSupported = (): boolean => typeof BroadcastChannel !== 'undefined';

/**
 * Sends OAuth result from the callback popup to other same-origin windows
 */
export const postGoogleSheetsOAuthMessage = (message: GoogleSheetsOAuthMessage): void => {
  if (!isBroadcastChannelSupported()) {
    return;
  }
  const channel = new BroadcastChannel(GOOGLE_SHEETS_OAUTH_CHANNEL);
  channel.postMessage(message);
  channel.close();
};

/**
 * Listens for OAuth results from the callback popup. Returns a cleanup function
 */
export const listenForGoogleSheetsOAuth = (onTokens: (raw: string) => void, onCancelled: () => void): (() => void) => {
  if (!isBroadcastChannelSupported()) {
    return () => {};
  }

  const channel = new BroadcastChannel(GOOGLE_SHEETS_OAUTH_CHANNEL);

  const onMessage = (event: MessageEvent<GoogleSheetsOAuthMessage>) => {
    const data = event.data;
    if (!data || typeof data !== 'object' || !('type' in data)) {
      return;
    }

    if (data.type === 'tokens') {
      onTokens(JSON.stringify(data.payload));
      return;
    }

    if (data.type === 'cancelled') {
      onCancelled();
    }
  };

  channel.addEventListener('message', onMessage);

  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
};
